import { supabase } from '../supabase';
import {
  RetailPrice,
  RetailPriceScrape,
  RetailTargetSku,
} from '../utils/types';

/** List all retail SKUs for a given species, ordered by country/retailer/tier. */
export const listSkusBySpecies = async (
  speciesId: number
): Promise<RetailTargetSku[]> => {
  const { data, error } = await supabase
    .from('retail_target_skus')
    .select('*')
    .eq('species_id', speciesId)
    .order('country', { ascending: true })
    .order('retailer', { ascending: true })
    .order('tier', { ascending: true, nullsFirst: false });

  if (error) throw new Error(error.message);
  return data ?? [];
};

export type CreateSkuPayload = Omit<
  RetailTargetSku,
  'id' | 'created_at' | 'last_scraped_at'
>;

export const createSku = async (
  payload: CreateSkuPayload
): Promise<RetailTargetSku> => {
  const { data, error } = await supabase
    .from('retail_target_skus')
    .insert(payload)
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export type UpdateSkuPayload = Partial<
  Omit<RetailTargetSku, 'id' | 'created_at' | 'last_scraped_at'>
>;

export const updateSku = async (
  id: number,
  patch: UpdateSkuPayload
): Promise<RetailTargetSku> => {
  const { data, error } = await supabase
    .from('retail_target_skus')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  return data;
};

export const toggleSkuActive = async (
  id: number,
  active: boolean
): Promise<RetailTargetSku> => {
  return updateSku(id, { active });
};

export const deleteSku = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from('retail_target_skus')
    .delete()
    .eq('id', id);

  if (error) throw new Error(error.message);
};

export interface RetailSkuScrapeHistory {
  prices: RetailPrice[];
  scrapes: RetailPriceScrape[];
}

export const listSkuScrapeHistory = async (
  skuId: number
): Promise<RetailSkuScrapeHistory> => {
  const [pricesRes, scrapesRes] = await Promise.all([
    supabase
      .from('retail_prices')
      .select('*')
      .eq('target_sku_id', skuId)
      .order('captured_at', { ascending: false })
      .limit(50),
    supabase
      .from('retail_price_scrapes')
      .select(
        'id, created_at, target_sku_id, source_url, scraped_at, http_status, parsed, parsed_at, parse_error, parser_notes'
      )
      .eq('target_sku_id', skuId)
      .order('scraped_at', { ascending: false })
      .limit(50),
  ]);

  if (pricesRes.error) throw new Error(pricesRes.error.message);
  if (scrapesRes.error) throw new Error(scrapesRes.error.message);

  return {
    prices: (pricesRes.data ?? []) as RetailPrice[],
    scrapes: (scrapesRes.data ?? []) as RetailPriceScrape[],
  };
};

export const listPendingSkuScrapeIds = async (
  skuIds: number[],
  withinMinutes = 30
): Promise<number[]> => {
  if (skuIds.length === 0) return [];

  const cutoff = new Date(Date.now() - withinMinutes * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('retail_price_scrapes')
    .select('target_sku_id')
    .in('target_sku_id', skuIds)
    .eq('parsed', false)
    .is('parse_error', null)
    .gte('scraped_at', cutoff);

  if (error) throw new Error(error.message);

  return Array.from(
    new Set((data ?? []).map((row) => Number(row.target_sku_id)))
  );
};

/**
 * Count how many price points (retail_prices rows) each SKU has.
 *
 * Returns a Map keyed by sku_id. Uses an exact head-count per SKU
 * (no rows transferred) so it stays accurate as the time series grows
 * past PostgREST's default row cap.
 */
export const countPricePointsForSkus = async (
  skuIds: number[]
): Promise<Map<number, number>> => {
  const out = new Map<number, number>();
  if (skuIds.length === 0) return out;

  await Promise.all(
    skuIds.map(async (id) => {
      const { count, error } = await supabase
        .from('retail_prices')
        .select('id', { count: 'exact', head: true })
        .eq('target_sku_id', id);
      if (!error) out.set(id, count ?? 0);
    })
  );

  return out;
};

/**
 * Outcome of the most recent scrape attempt for a SKU. Drives the
 * status chip in the admin "SKUs Retail" table.
 *
 *   priced       → Firecrawl + Claude OK, retail_prices row written
 *   no_product   → scraped + parsed but Claude said product_found=false
 *                  (OOS, login wall, page broken, wrong species…)
 *   scrape_error → Firecrawl returned an error / Claude threw
 *   pending      → row exists but parsed=false (in flight or stuck)
 *   none         → no scrape attempts ever
 */
export type LastScrapeOutcome =
  | 'priced'
  | 'no_product'
  | 'scrape_error'
  | 'pending'
  | 'none';

export interface LastScrapeStatus {
  outcome: LastScrapeOutcome;
  scraped_at: string;
  parse_error: string | null;
  http_status: number | null;
}

/**
 * Fetch the outcome of each SKU's most recent scrape attempt.
 *
 * Returns a Map keyed by sku_id. SKUs without any scrapes are absent
 * from the map (callers should default to 'none').
 *
 * Implementation: pulls recent scrape rows for all requested SKUs in
 * a single query (last 30 days, capped at 500 rows) and reduces in
 * JS to the latest per SKU. This avoids a custom RPC / view while
 * staying fast for the admin table sizes we have (~70 SKUs today).
 */
export const listLatestScrapeStatusForSkus = async (
  skuIds: number[]
): Promise<Map<number, LastScrapeStatus>> => {
  const out = new Map<number, LastScrapeStatus>();
  if (skuIds.length === 0) return out;

  const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data: scrapes, error } = await supabase
    .from('retail_price_scrapes')
    .select(
      'id, target_sku_id, scraped_at, http_status, parsed, parse_error'
    )
    .in('target_sku_id', skuIds)
    .gte('scraped_at', since)
    .order('scraped_at', { ascending: false })
    .limit(500);

  if (error) throw new Error(error.message);
  if (!scrapes?.length) return out;

  // Pick the most recent row per SKU
  const latestByScrape = new Map<number, typeof scrapes[number]>();
  for (const row of scrapes) {
    const sid = Number(row.target_sku_id);
    if (!latestByScrape.has(sid)) latestByScrape.set(sid, row);
  }

  // Which of those latest scrapes produced a retail_prices row with a
  // non-null price? We need that to distinguish priced vs no_product.
  const latestIds = Array.from(latestByScrape.values()).map((r) => r.id);
  const pricedScrapeIds = new Set<number>();
  if (latestIds.length) {
    const { data: priced } = await supabase
      .from('retail_prices')
      .select('raw_scrape_id')
      .in('raw_scrape_id', latestIds)
      .not('price_local', 'is', null);
    for (const p of priced ?? []) pricedScrapeIds.add(Number(p.raw_scrape_id));
  }

  for (const [skuId, row] of latestByScrape) {
    let outcome: LastScrapeOutcome;
    if (row.parse_error) outcome = 'scrape_error';
    else if (!row.parsed) outcome = 'pending';
    else if (pricedScrapeIds.has(Number(row.id))) outcome = 'priced';
    else outcome = 'no_product';

    out.set(skuId, {
      outcome,
      scraped_at: row.scraped_at,
      parse_error: row.parse_error,
      http_status: row.http_status,
    });
  }

  return out;
};

export interface CreateManualPricePayload {
  sku: RetailTargetSku;
  /** Total price the customer pays, in the SKU's local currency. */
  priceLocal: number;
  /** Package size value the operator read (e.g. 500, 1, 3). */
  packageSizeValue: number;
  /** Package size unit (kg, g, lb, oz, pieza, pack, pcs). */
  packageSizeUnit: string;
  /** Raw package label, optional (auto-built from value+unit if empty). */
  packageSizeText?: string | null;
  /** Computed weight in kg (from convertToKg). */
  weightKg: number | null;
  weightSource: string | null;
  /** Computed USD/kg (from computeUsdPerKg). */
  priceUsdPerKg: number | null;
  /** FX rate used (currency per 1 USD). */
  fxRateUsed: number | null;
  originCountry?: string | null;
  onPromo?: boolean;
  /** Defaults to now. */
  capturedAt?: string;
  notes?: string | null;
}

/**
 * Insert a manually-entered price point for a SKU the bot can't scrape.
 *
 * Writes a shell retail_price_scrapes row (no markdown, parsed=true,
 * parser_notes explaining it's manual) to satisfy the NOT NULL
 * raw_scrape_id FK, then the curated retail_prices row tagged
 * entry_method='manual'.
 *
 * Requires the superadmin INSERT RLS policy (see migration
 * 20260610183942_retail_prices_manual_entry.sql).
 */
export const createManualRetailPrice = async (
  payload: CreateManualPricePayload
): Promise<RetailPrice> => {
  const { sku } = payload;
  const capturedAt = payload.capturedAt ?? new Date().toISOString();

  // 1. Shell scrape row for lineage + FK
  const { data: scrapeRow, error: scrapeErr } = await supabase
    .from('retail_price_scrapes')
    .insert({
      target_sku_id: sku.id,
      source_url: sku.url,
      scraped_at: capturedAt,
      http_status: null,
      payload_md: null,
      parsed: true,
      parsed_at: capturedAt,
      parser_notes: `Entrada manual${payload.notes ? `: ${payload.notes}` : ''}`,
    })
    .select('id')
    .single();

  if (scrapeErr || !scrapeRow) {
    throw new Error(
      `No se pudo crear el registro de scrape: ${scrapeErr?.message}`
    );
  }

  // 2. Curated manual price row
  const { data: priceRow, error: priceErr } = await supabase
    .from('retail_prices')
    .insert({
      raw_scrape_id: scrapeRow.id,
      target_sku_id: sku.id,
      captured_at: capturedAt,
      species_id: sku.species_id,
      retailer: sku.retailer,
      country: sku.country,
      market_id: sku.market_id,
      tier: sku.tier,
      product_name: sku.product_name,
      origin_country: payload.originCountry ?? null,
      on_promo: payload.onPromo ?? false,
      price_local: payload.priceLocal,
      currency: sku.currency,
      package_size_text:
        payload.packageSizeText ||
        `${payload.packageSizeValue} ${payload.packageSizeUnit}`,
      package_size_value: payload.packageSizeValue,
      package_size_unit: payload.packageSizeUnit,
      weight_kg: payload.weightKg,
      weight_source: payload.weightSource,
      price_usd_per_kg: payload.priceUsdPerKg,
      fx_rate_used: payload.fxRateUsed,
      entry_method: 'manual',
    })
    .select('*')
    .single();

  if (priceErr || !priceRow) {
    throw new Error(`No se pudo guardar el precio manual: ${priceErr?.message}`);
  }

  // Keep the SKU's last_scraped_at fresh so the table reflects the entry
  await supabase
    .from('retail_target_skus')
    .update({ last_scraped_at: capturedAt })
    .eq('id', sku.id);

  return priceRow as RetailPrice;
};

export interface UpdateRetailPricePayload {
  /** Total price the customer pays, in the row's local currency. */
  priceLocal: number;
  packageSizeValue: number;
  packageSizeUnit: string;
  packageSizeText?: string | null;
  weightKg: number | null;
  weightSource: string | null;
  priceUsdPerKg: number | null;
  fxRateUsed: number | null;
  originCountry?: string | null;
  onPromo?: boolean;
  /** Optional new capture timestamp (ISO). Leave undefined to keep it. */
  capturedAt?: string;
}

/**
 * Edit an existing retail_prices row (manual correction from the
 * superadmin UI). Only touches the price / package / computed columns —
 * the denormalized snapshots (retailer, country, species…) and
 * entry_method are left as-is.
 *
 * Requires the superadmin UPDATE RLS policy (see migration
 * 20260622120000_retail_prices_superadmin_update_delete.sql).
 */
export const updateRetailPrice = async (
  id: number,
  payload: UpdateRetailPricePayload
): Promise<RetailPrice> => {
  const patch: Record<string, unknown> = {
    price_local: payload.priceLocal,
    package_size_value: payload.packageSizeValue,
    package_size_unit: payload.packageSizeUnit,
    package_size_text:
      payload.packageSizeText ||
      `${payload.packageSizeValue} ${payload.packageSizeUnit}`,
    weight_kg: payload.weightKg,
    weight_source: payload.weightSource,
    price_usd_per_kg: payload.priceUsdPerKg,
    fx_rate_used: payload.fxRateUsed,
    origin_country: payload.originCountry ?? null,
    on_promo: payload.onPromo ?? false,
  };
  if (payload.capturedAt) patch.captured_at = payload.capturedAt;

  const { data, error } = await supabase
    .from('retail_prices')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(error.message);
  return data as RetailPrice;
};

/**
 * Delete a single retail_prices row (remove a bad data point from the
 * time series). The shell/raw scrape row is left untouched.
 *
 * Requires the superadmin DELETE RLS policy.
 */
export const deleteRetailPrice = async (id: number): Promise<void> => {
  const { error } = await supabase.from('retail_prices').delete().eq('id', id);
  if (error) throw new Error(error.message);
};

/**
 * Trigger a one-off scrape of a specific SKU via the ingest-retail-prices
 * edge function. Used by the "Test scrape" button in the admin UI.
 *
 * When `dryRun` is true the function still does Firecrawl + Claude (to
 * compute everything) but skips all DB writes. Useful for previewing what
 * a new SKU URL would extract without polluting the time-series.
 */
export const triggerSkuScrape = async (
  skuId: number,
  options: { dryRun?: boolean } = {}
) => {
  const { data, error } = await supabase.functions.invoke(
    'ingest-retail-prices',
    {
      body: {
        sku_ids: [skuId],
        dry_run: options.dryRun ?? false,
      },
    }
  );
  if (error) throw new Error(error.message);
  return data;
};
